/**
 * VendorRequests.jsx — Iter 65. Service vendor's side of the guided founder flow.
 *
 * A founder picks this vendor for a product, signs the three-party agreement,
 * then submits their documents + company details. That lands here as a request
 * awaiting a quote. The vendor reads the details, downloads the documents, and
 * returns a final service charge + govt fees. Founder approves and pays; the
 * card then shows as cleared to start.
 *
 * Requests awaiting a quote are pinned to the top — those are the ones with a
 * founder waiting on the other end.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { useCurrency } from '@/hooks/useCurrency';
import { formatDate } from '@/utils/datetime';
import {
  Briefcase, CaretDown, ChatCircleDots, Check, CheckCircle, Clock, CurrencyInr,
  DownloadSimple, FileText, Lock, PaperPlaneTilt, Receipt, ShieldCheck, Star,
  UploadSimple, Warning, X,
} from '@phosphor-icons/react';
import ServiceChat from '@/components/ServiceChat';
import ProgressDonut from '@/components/ProgressDonut';

const STATUS_META = {
  agreement_signed: { label: 'Founder preparing documents', tone: 'muted' },
  awaiting_quote:   { label: 'Awaiting your quote',         tone: 'warning' },
  quote_ready:      { label: 'Quote sent — with founder',   tone: 'info' },
  pending_payment:  { label: 'Approved — awaiting payment',  tone: 'info' },
  active:           { label: 'Cleared to start',            tone: 'success' },
  delivered:        { label: 'Delivered — with founder for review', tone: 'info' },
  revisions:        { label: 'Changes requested',           tone: 'warning' },
  awaiting_balance: { label: 'Approved — balance due',      tone: 'info' },
  closed:           { label: 'Completed',                   tone: 'success' },
};

const TONES = {
  warning: 'bg-warning/15 text-warning',
  info:    'bg-info/15 text-info',
  success: 'bg-success/15 text-success',
  muted:   'bg-text-muted/15 text-text-muted',
};

const VendorRequests = ({ accent }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async (opts = {}) => {
    try {
      const r = await api.get('/service-requests/vendor-queue');
      setRequests(r.data.requests || []);
    } catch (e) {
      if (!opts.silent) toast.error(e.response?.data?.detail || 'Could not load requests');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live-refresh the queue so a new request, a quote acceptance or the founder's
  // review decision lands without a manual reload. Skipped while the tab is hidden.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') load({ silent: true });
    }, 20000);
    return () => clearInterval(id);
  }, [load]);

  // Opening a thread marks it read server-side; mirror it so the badge clears
  // without refetching the whole queue. Same array back when there's nothing to
  // clear — this fires on every poll tick, not just the first read.
  const clearUnread = useCallback((id) => {
    setRequests((prev) => (prev.some((r) => r.id === id && (r.chat?.unread || 0) > 0)
      ? prev.map((r) => (r.id === id ? { ...r, chat: { ...r.chat, unread: 0 } } : r))
      : prev));
  }, []);

  const awaiting = requests.filter((r) => r.status === 'awaiting_quote').length;
  const unread = requests.reduce((s, r) => s + (r.chat?.unread || 0), 0);

  const selected = requests.find((r) => r.id === expanded) || null;

  if (loading) return <div className="p-6 text-sm text-text-muted">Loading requests…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="vendor-requests-screen">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2" style={{ color: accent }}>Managed requests</div>
        <h1 className="text-3xl font-heading font-bold text-text-primary leading-[1.05] mb-2" style={{ letterSpacing: '-0.02em' }}>
          Service requests <span className="text-text-muted text-base font-normal ml-1">({requests.length})</span>
        </h1>
        <p className="text-sm text-text-secondary">
          Founders who selected you for a specific product and signed the agreement. Their documents and contact
          details unseal once payment clears &mdash; quote against your listed scope.
          {awaiting > 0 && (
            <strong className="text-warning"> {awaiting} waiting on your quote.</strong>
          )}
          {unread > 0 && (
            <strong style={{ color: accent }} data-testid="vendor-requests-unread"> {unread} unread message{unread === 1 ? '' : 's'}.</strong>
          )}
        </p>
      </div>

      {requests.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl p-10 text-center text-sm text-text-muted bg-surface" data-testid="vendor-requests-empty">
          <Briefcase size={26} className="mx-auto mb-2 text-text-muted" />
          No requests yet. Founders reach you by picking one of your catalog products in the services marketplace —
          keep your pricing and required documents up to date in <strong className="text-text-primary">Catalog</strong>.
        </div>
      ) : (
        // Queue left, conversation docked right. Below `xl` the rail drops under
        // the list rather than squeezing both into an unreadable width.
        // 65 / 35 split, matching the founder engagements page. flex-grow ratios
        // rather than percentage widths so the gap is absorbed.
        <div className="flex flex-col xl:flex-row gap-4 items-start">
          <div className="w-full min-w-0 space-y-3 xl:flex-[65_1_0%]">
            {requests.map((r) => (
              <RequestCard key={r.id} req={r} accent={accent}
                open={expanded === r.id}
                dimmed={!!expanded && expanded !== r.id}
                onToggle={() => setExpanded(expanded === r.id ? null : r.id)}
                onChanged={load} />
            ))}
          </div>
          <ChatRail req={selected} accent={accent}
            onClose={() => setExpanded(null)}
            onRead={() => selected && clearUnread(selected.id)} />
        </div>
      )}
    </div>
  );
};

/**
 * The docked conversation. Keyed on the request id so switching cards tears the
 * thread down and remounts it — no message list, draft or scroll position can
 * survive from the previous founder's thread into this one.
 */
const ChatRail = ({ req, accent, onClose, onRead }) => (
  <aside className="w-full min-w-0 xl:flex-[35_1_0%] xl:sticky xl:top-6" data-testid="vendor-chat-rail">
    <div className="bg-surface border border-border rounded-xl p-4">
      {!req ? (
        <div className="text-center py-8" data-testid="vendor-chat-rail-empty">
          <ChatCircleDots size={24} className="mx-auto mb-2 text-text-muted" />
          <div className="text-sm font-semibold text-text-primary">No request selected</div>
          <p className="text-[11px] text-text-muted mt-1 leading-relaxed">
            Open a request on the left to message that founder. Each request has its own thread.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">Conversation</div>
              <div className="text-sm font-heading font-bold text-text-primary truncate">
                {req.offering?.catalog_item_name || 'Service request'}
              </div>
            </div>
            <button onClick={onClose} data-testid="vendor-chat-rail-close"
              className="text-text-muted hover:text-text-primary flex-shrink-0" aria-label="Close conversation">
              <X size={14} weight="bold" />
            </button>
          </div>
          {req.chat?.open ? (
            <ServiceChat key={req.id} requestId={req.id} accent={accent}
              counterpartyFallback={req.founder_startup_name || 'Founder'}
              onRead={onRead} compact />
          ) : (
            <div className="text-[11px] text-text-muted leading-relaxed py-4" data-testid="vendor-chat-rail-locked">
              This founder hasn&rsquo;t signed the agreement yet, so there&rsquo;s no thread to open.
            </div>
          )}
        </>
      )}
    </div>
  </aside>
);

const RequestCard = ({ req, accent, open, onToggle, onChanged, dimmed }) => {
  const { format } = useCurrency();
  const meta = STATUS_META[req.status] || { label: req.status, tone: 'muted' };
  const offering = req.offering || {};
  const quote = req.quote || {};
  const currency = offering.currency || 'INR';
  const needsQuote = req.status === 'awaiting_quote';
  const unread = req.chat?.unread || 0;
  const pct = req.progress?.pct ?? 0;
  const finished = req.status === 'closed';

  // Same reasoning as the founder tile: selection and hover are drawn in the
  // vendor accent, not the grey theme tokens, because `text-primary` is near-white
  // and `hover:bg-background/40` has no light-theme override — both looked grey or
  // invisible on a light background.
  const borderClass = open ? ''                       // colour comes from selectedStyle
    : needsQuote ? 'border-warning/40'
    : 'border-border';
  const selectedStyle = open
    ? { borderColor: accent, boxShadow: `0 0 0 1px ${accent}55, 0 10px 30px -14px ${accent}80` }
    : undefined;

  return (
    // Dimmed = another request owns the conversation on the right. Still
    // clickable, so switching threads stays one click.
    <div
      className={`bg-surface border rounded-xl overflow-hidden transition-all duration-200 ${borderClass} ${
        dimmed ? 'opacity-40 blur-[1.5px] saturate-50 hover:opacity-70 hover:blur-0' : ''
      }`}
      style={selectedStyle}
      data-testid={`vendor-request-${req.id}`}
      data-dimmed={dimmed ? 'true' : 'false'}>
      <button onClick={onToggle} aria-expanded={open}
        style={{ '--tile-hover': `${accent}14`, '--tile-open': open ? `${accent}0d` : 'transparent' }}
        className="w-full text-left p-4 flex items-start gap-3 flex-wrap transition-colors bg-[var(--tile-open)] hover:bg-[var(--tile-hover)]"
        data-testid={`vendor-request-toggle-${req.id}`}>
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-[17px] font-heading font-bold text-text-primary">{offering.catalog_item_name}</h3>
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${TONES[meta.tone]}`}>{meta.label}</span>
            {unread > 0 && (
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded inline-flex items-center gap-1"
                style={{ backgroundColor: `${accent}22`, color: accent }}
                data-testid={`vendor-request-unread-${req.id}`}>
                <ChatCircleDots size={10} weight="fill" /> {unread}
              </span>
            )}
          </div>
          <div className="text-[14px] text-text-secondary mt-1">
            {req.founder_startup_name || req.founder_full_name || 'Founder'}
          </div>
          <div className="flex items-center gap-3 text-[11.5px] text-text-muted mt-1.5 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <CurrencyInr size={10} weight="bold" /> Listed {format(offering.total_price || 0, currency)}
            </span>
            {quote.total != null && (
              <span className="inline-flex items-center gap-1" style={{ color: accent }}>
                <Receipt size={10} weight="bold" /> Quoted {format(quote.total, currency)}
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <FileText size={10} weight="bold" /> {(req.required_documents || []).length} doc{(req.required_documents || []).length === 1 ? '' : 's'}
            </span>
            {req.docs_submitted_at && (
              <span className="inline-flex items-center gap-1"><Clock size={10} weight="bold" /> Submitted {formatDate(req.docs_submitted_at)}</span>
            )}
          </div>
        </div>
        {/* Same progress ring the founder sees, so both sides read one number. */}
        <div className="flex items-center gap-2.5 flex-shrink-0">
          <div className="text-right hidden sm:block">
            <div className="text-[12px] font-semibold text-text-primary leading-tight">
              {req.progress?.total_steps
                ? <>Step {Math.min((req.progress.done_steps || 0) + (finished ? 0 : 1), req.progress.total_steps)} of {req.progress.total_steps}</>
                : meta.label}
            </div>
            <div className="text-[11px] text-text-muted leading-tight">
              {req.progress?.current_label || (finished ? 'Complete' : '')}
            </div>
          </div>
          <ProgressDonut pct={pct} size={56} stroke={5.5}
            color={finished ? '#22c55e' : accent}
            testid={`vendor-request-progress-${req.id}`} />
          <CaretDown size={14} weight="bold" className="text-text-muted"
            style={{ transform: open ? 'rotate(180deg)' : 'none' }} />
        </div>
      </button>

      {open && (
        <div className="border-t border-border p-4 space-y-4">
          {!req.docs_submitted_at ? (
            <div className="text-xs text-text-muted inline-flex items-center gap-2">
              <Lock size={12} weight="fill" />
              The founder is still assembling their documents.
            </div>
          ) : (
            <>
              {/* (Iter 67) Everything identifying stays sealed until payment. */}
              {req.docs_locked && <SealedNotice req={req} accent={accent} />}
              <FounderDetails req={req} />
              <DocumentList req={req} accent={accent} />
            </>
          )}

          {req.status === 'awaiting_quote' && (
            <QuoteForm req={req} accent={accent} onSaved={onChanged} />
          )}

          {quote.submitted_at && req.status !== 'awaiting_quote' && (
            <div className="rounded-lg border border-border bg-background p-3" data-testid={`vendor-request-quote-${req.id}`}>
              <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Your quote</div>
              <div className="text-sm text-text-secondary space-y-0.5">
                <div>Service charge: <strong className="text-text-primary font-mono">{format(quote.service_charge || 0, currency)}</strong></div>
                {(quote.gst_amount || 0) > 0 && <div>GST ({quote.gst_pct || 18}%): <strong className="text-text-primary font-mono">{format(quote.gst_amount, currency)}</strong></div>}
                {(quote.govt_fees || 0) > 0 && <div>Govt fees: <strong className="text-text-primary font-mono">{format(quote.govt_fees, currency)}</strong></div>}
                <div>Total: <strong className="font-mono" style={{ color: accent }}>{format(quote.total || 0, currency)}</strong></div>
                {quote.delivery_days && <div className="text-[11px] text-text-muted">Delivery in {quote.delivery_days} days from payment</div>}
              </div>
              {quote.accepted_at && (
                <div className="text-[11px] mt-2 inline-flex items-center gap-1.5 text-success">
                  <CheckCircle size={12} weight="fill" /> Approved by the founder on {formatDate(quote.accepted_at)}
                </div>
              )}
              {req.status === 'pending_payment' && (
                <div className="text-[11px] mt-1.5 text-text-muted inline-flex items-center gap-1.5">
                  <Clock size={11} weight="bold" /> Waiting on payment — you&rsquo;ll be emailed the moment it clears.
                </div>
              )}
              {req.status === 'active' && (
                <div className="text-[11px] mt-1.5 inline-flex items-center gap-1.5" style={{ color: accent }}>
                  <Check size={11} weight="bold" /> Paid{req.invoice_number ? ` · invoice ${req.invoice_number}` : ''} — cleared to start.
                </div>
              )}
            </div>
          )}

          {['active', 'revisions', 'delivered', 'awaiting_balance', 'closed'].includes(req.status) && (
            <DeliveryPanel req={req} accent={accent} onChanged={onChanged} />
          )}
        </div>
      )}
    </div>
  );
};

/**
 * (Iter 68) The partner's side of the delivery loop. Once the request is active
 * the partner uploads one or more final documents and marks them delivered; if the
 * founder asks for changes the request comes back here as `revisions` and the
 * partner re-delivers. The founder's approval (with a rating) closes it.
 */
const DeliveryPanel = ({ req, accent, onChanged }) => {
  const [staged, setStaged] = useState([]);   // [{file_id, filename}] not yet delivered
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const delivery = req.delivery || {};
  const deliveredFiles = delivery.files || [];
  const canDeliver = req.status === 'active' || req.status === 'revisions';

  const upload = async (list) => {
    if (!list.length) return;
    setUploading(true);
    try {
      const added = [];
      for (const file of list) {
        const fd = new FormData();
        fd.append('file', file);
        const up = await api.post('/files/upload', fd);
        added.push({ file_id: up.data.file_id, filename: up.data.filename });
      }
      setStaged((prev) => [...prev, ...added]);
      toast.success(`${added.length} file${added.length === 1 ? '' : 's'} ready to deliver.`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const removeStaged = (fileId) => setStaged((prev) => prev.filter((f) => f.file_id !== fileId));

  const deliver = async () => {
    if (!staged.length) { toast.error('Upload at least one document first.'); return; }
    setBusy(true);
    try {
      await api.post(`/service-requests/${req.id}/deliver`, {
        file_ids: staged.map((f) => f.file_id), note,
      });
      toast.success('Delivered — the founder has been notified.');
      setStaged([]); setNote('');
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not deliver'); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border p-3" style={{ borderColor: `${accent}55`, backgroundColor: `${accent}0d` }}
      data-testid={`vendor-request-delivery-${req.id}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: accent }}>
        {canDeliver ? 'Deliver the final documents' : 'Delivery'}
      </div>

      {deliveredFiles.length > 0 && (
        <div className="mb-3 text-[12px] text-text-secondary">
          <span className="text-text-muted">Delivered: </span>
          {deliveredFiles.map((f) => f.filename).join(', ')}
          {delivery.delivered_at && <span className="text-text-muted"> · {formatDate(delivery.delivered_at)}</span>}
        </div>
      )}

      {req.status === 'revisions' && req.last_revision_note && (
        <div className="mb-3 rounded border border-warning/40 bg-warning/5 p-2 text-[12px] text-text-secondary"
          data-testid={`vendor-request-revision-${req.id}`}>
          <strong className="text-text-primary">Changes requested: </strong>{req.last_revision_note}
        </div>
      )}

      {canDeliver ? (
        <>
          {staged.length > 0 && (
            <div className="mb-2 space-y-1">
              {staged.map((f) => (
                <div key={f.file_id} className="flex items-center gap-2 text-[12px] bg-background border border-border rounded-lg px-2.5 py-1.5"
                  data-testid={`vendor-request-staged-${f.file_id}`}>
                  <FileText size={11} weight="bold" className="text-text-muted flex-shrink-0" />
                  <span className="flex-1 truncate text-text-primary">{f.filename}</span>
                  <button onClick={() => removeStaged(f.file_id)} className="text-text-muted hover:text-danger" aria-label="Remove">
                    <X size={11} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <label className="text-[11px] inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg cursor-pointer border border-border hover:border-text-primary"
            data-testid={`vendor-request-deliver-upload-${req.id}`}>
            <UploadSimple size={11} weight="bold" />
            {uploading ? 'Uploading…' : (staged.length ? 'Add another file' : 'Choose final documents')}
            <input type="file" multiple className="hidden" disabled={uploading}
              onChange={(e) => { upload(Array.from(e.target.files || [])); e.target.value = ''; }} />
          </label>
          <div className="mt-2">
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              data-testid={`vendor-request-deliver-note-${req.id}`}
              placeholder="Optional note for the founder"
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-text-primary" />
          </div>
          <button onClick={deliver} disabled={busy || !staged.length}
            data-testid={`vendor-request-deliver-submit-${req.id}`}
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-lg text-white disabled:opacity-50"
            style={{ backgroundColor: accent }}>
            <PaperPlaneTilt size={11} weight="bold" />
            {busy ? 'Delivering…' : (deliveredFiles.length ? 'Re-deliver' : 'Mark as delivered')}
          </button>
          <div className="text-[10px] text-text-muted mt-1.5">
            Add one or more files. The founder previews each; if you deliver several they arrive in their vault as a single ZIP once approved.
          </div>
        </>
      ) : req.status === 'delivered' ? (
        <div className="text-[12px] text-text-muted">Delivered — the founder is reviewing it.</div>
      ) : req.status === 'awaiting_balance' ? (
        <div className="text-[12px] text-text-muted">Approved — waiting on the founder&rsquo;s balance payment.</div>
      ) : req.status === 'closed' ? (
        <div className="text-[12px] inline-flex items-center gap-1.5" style={{ color: accent }}
          data-testid={`vendor-request-rating-${req.id}`}>
          <Star size={12} weight="fill" style={{ color: '#f59e0b' }} />
          {req.completion_rating ? `Rated ${req.completion_rating}/5 by the founder — completed.` : 'Completed.'}
        </div>
      ) : null}
    </div>
  );
};

/**
 * (Iter 67) Why the details below are redacted, and what unseals them. Stated
 * once, at the top of the panel, so the masked values read as policy rather than
 * as missing data the vendor should chase.
 */
const SealedNotice = ({ req, accent }) => (
  <div className="rounded-lg p-3 flex items-start gap-2.5"
    style={{ backgroundColor: `${accent}0f`, border: `1px solid ${accent}44` }}
    data-testid={`vendor-request-sealed-${req.id}`}>
    <ShieldCheck size={15} weight="fill" style={{ color: accent }} className="flex-shrink-0 mt-0.5" />
    <div>
      <div className="text-[12px] font-semibold text-text-primary">
        Contact and identification details are sealed until payment clears
      </div>
      <p className="text-[11px] text-text-muted leading-relaxed mt-0.5">
        Quote against your listed scope and price. The moment the founder pays, their full details and every
        document below unlock here &mdash; and you&rsquo;re cleared to start. Use the conversation on the right if you
        need to check anything before quoting.
      </p>
    </div>
  </div>
);

const FounderDetails = ({ req }) => {
  const fields = req.data_fields || [];
  const data = req.data || {};
  const locked = !!req.docs_locked;
  if (!fields.length) return null;
  return (
    <div className="rounded-lg border border-border bg-background p-3" data-testid={`vendor-request-details-${req.id}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-2 flex items-center gap-1.5">
        Founder details
        {locked && <Lock size={9} weight="fill" className="text-text-muted" />}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
        {fields.map((f) => {
          // Aadhaar is never stored in full — even unsealed, the number itself is
          // read off the Aadhaar document in the founder's vault below.
          const value = f.id === 'aadhaar'
            ? (data.aadhaar_last4 ? `XXXX XXXX ${data.aadhaar_last4}` : null)
            : data[f.id];
          if (value === undefined || value === null || value === '') return null;
          // The server sends the redacted string, so this is purely about styling
          // a masked value differently from a real one.
          const masked = locked && f.vendor_masked;
          return (
            <div key={f.id} className="text-[12px]">
              <span className="text-text-muted">{f.label}: </span>
              <span className={masked ? 'text-text-muted font-mono select-none' : 'text-text-primary font-medium'}>
                {String(value)}
              </span>
            </div>
          );
        })}
      </div>
      {req.note && (
        <div className="mt-2 pt-2 border-t border-border text-[12px] text-text-secondary">
          <span className="text-text-muted">Note: </span>{req.note}
        </div>
      )}
    </div>
  );
};

const DocumentList = ({ req, accent }) => {
  const docs = req.required_documents || [];
  const [busy, setBusy] = useState('');
  if (!docs.length) return null;

  const download = async (file) => {
    setBusy(file.id);
    try {
      const r = await api.get(`/marketplace-engagements/${req.id}/docs/${file.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Download failed'); }
    finally { setBusy(''); }
  };

  const locked = !!req.docs_locked;
  return (
    <div className="rounded-lg border border-border bg-background p-3" data-testid={`vendor-request-docs-${req.id}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-2 flex items-center gap-1.5">
        Documents ({docs.length})
        {locked && <Lock size={9} weight="fill" className="text-text-muted" />}
      </div>
      <div className="space-y-1.5">
        {docs.map((d) => (
          <div key={`${d.dataroom_id}::${d.slot_id}`} className="flex items-center gap-2 flex-wrap">
            {d.filled
              ? <Check size={11} weight="bold" style={{ color: accent }} className="flex-shrink-0" />
              : <Warning size={11} weight="fill" className={`${d.optional ? 'text-text-muted' : 'text-warning'} flex-shrink-0`} />}
            <span className="text-[12px] text-text-primary flex-1 min-w-[120px]">
              {d.slot_name}
              {d.optional && <span className="ml-1.5 text-[9px] uppercase tracking-wider font-bold text-text-muted">Optional</span>}
            </span>
            {/* Locked: the server sends no filenames (they leak names too), only
                a count — so show that the slot is satisfied and nothing more. */}
            {locked ? (
              <span className="text-[11px] text-text-muted inline-flex items-center gap-1 flex-shrink-0">
                <Lock size={9} weight="fill" />
                {d.filled ? `${d.file_count || 1} file${(d.file_count || 1) === 1 ? '' : 's'} — unlocks on payment` : 'not uploaded'}
              </span>
            ) : (d.files || []).map((f) => (
              <button key={f.id} onClick={() => download(f)} disabled={busy === f.id}
                data-testid={`vendor-request-download-${f.id}`}
                className="text-[11px] font-semibold px-2 py-1 rounded border border-border text-text-secondary hover:text-text-primary inline-flex items-center gap-1 disabled:opacity-50">
                <DownloadSimple size={10} weight="bold" /> {busy === f.id ? 'Preparing…' : (f.filename || 'file')}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const QuoteForm = ({ req, accent, onSaved }) => {
  const { format } = useCurrency();
  const offering = req.offering || {};
  const currency = offering.currency || 'INR';
  // Pre-filled from the listing: most requests are quoted at exactly the listed
  // price, so the vendor usually just confirms.
  const [serviceCharge, setServiceCharge] = useState(offering.gross_price ?? '');
  const [govtFees, setGovtFees] = useState(offering.govt_fees ?? 0);
  const [deliveryDays, setDeliveryDays] = useState(offering.delivery_days ?? '');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const GST_PCT = 18;
  const preTaxTotal = Number(serviceCharge || 0) + Number(govtFees || 0);
  const gstAmount = Math.round(Number(serviceCharge || 0) * GST_PCT) / 100;
  const total = preTaxTotal + gstAmount;

  const submit = async () => {
    if (serviceCharge === '' || Number(serviceCharge) < 0) { toast.error('Enter your service charge'); return; }
    setBusy(true);
    try {
      await api.post(`/service-requests/${req.id}/quote`, {
        service_charge: Number(serviceCharge),
        govt_fees: Number(govtFees || 0),
        delivery_days: deliveryDays ? Number(deliveryDays) : null,
        notes,
      });
      toast.success('Quote sent to the founder.');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send the quote'); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border p-3" style={{ borderColor: `${accent}55`, backgroundColor: `${accent}0d` }}
      data-testid={`vendor-request-quote-form-${req.id}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: accent }}>Send your final quote</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label={`Service charge (${currency}) *`} value={serviceCharge} onChange={setServiceCharge} type="number"
          testid={`vendor-quote-service-${req.id}`} />
        <Field label={`Govt / statutory fees (${currency})`} value={govtFees} onChange={setGovtFees} type="number"
          testid={`vendor-quote-govt-${req.id}`} />
        <Field label="Delivery days" value={deliveryDays} onChange={setDeliveryDays} type="number"
          testid={`vendor-quote-days-${req.id}`} />
      </div>
      <div className="mt-3">
        <label className="block text-[10px] uppercase tracking-wider font-bold text-text-muted mb-1">Note to the founder (optional)</label>
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
          data-testid={`vendor-quote-notes-${req.id}`}
          placeholder="e.g. Revised because the DIN needs a fresh KYC filing first."
          className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-text-primary" />
      </div>
      <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
        <div className="text-[11px] text-text-muted">
          Founder pays <strong className="font-mono" style={{ color: accent }}>{format(total, currency)}</strong>
          {' '}(incl. {GST_PCT}% GST of {format(gstAmount, currency)})
          {offering.total_price != null && Math.abs(preTaxTotal - offering.total_price) > 0.5 && (
            <> · listed was {format(offering.total_price, currency)}</>
          )}
        </div>
        <button onClick={submit} disabled={busy}
          data-testid={`vendor-quote-submit-${req.id}`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-lg text-white disabled:opacity-50"
          style={{ backgroundColor: accent }}>
          <PaperPlaneTilt size={11} weight="bold" /> {busy ? 'Sending…' : 'Send quote'}
        </button>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', testid }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-wider font-bold text-text-muted mb-1">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-text-primary" />
  </div>
);

export default VendorRequests;
